import React, { useState, useEffect } from 'react';
import Helmet from 'react-helmet';
import classnames from 'classnames';
import { Cell, Row } from 'react-table';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { OrderWithSamples, SampleDetail } from '@models';
import routes from '@configs/routes.json';

import { IApplicationState } from '@store';
import { RequestStatus } from '@store/types';

import { HOCLoader } from '@components/HOCLoader/HOCLoader';

import useResultsTable from '../results-helpers/ResultsTable';

import {
  getColumns,
  SelectAndDownload,
} from '../results-helpers/SamplesListShared';
import { translateStatus, getHeadings } from './common';
import {
  StageProcessing,
  StageUploaded,
  StageFinished,
} from './common-components';
import useToast from '../notification/Toast';

import { useOrdersAPI } from '../../core/hooks/api/orders/useOrders';
import { formatDateWithTimezone } from '../../core/utils/dateTime';
import { InvalidMessage } from '../../core/components/';
import './OrderDetail.scss';

export interface IGetOrderReports {
  documentTypes: string[];
  orderTestIds: string[];
}

export interface IDownloadSampleReport {
  created: string;
  documentType: string;
  externalSubjectId: string;
  id: string;
  name: string;
  orderTestId: string;
  status: string;
}

export interface OrderDetailProps {
  order: OrderWithSamples | undefined;
  orderNumber: string;
}

const getCustomCellProps = (cell: Cell<SampleDetail, any>) => {
  if (
    cell.column.id === 'testType' &&
    cell.row.original.status === 'ProcessingWithWarning'
  ) {
    return { className: 'sample-type-warning' };
  } else {
    return {};
  }
};

const OrderDetail: React.FC<OrderDetailProps> = ({
  order: currentOrder,
  orderNumber,
}) => {
  const [hiddenColumns, setHiddenColumns] = useState(['status']);
  const [order, setOrder] = useState<any>(false);

  const [tableData, setTableData] = useState<any[]>([]);
  const [requestStatus, setRequestStatus] = useState(RequestStatus.inProgress);

  const { t } = useTranslation();
  const headings = getHeadings(t);
  const {
    getDetails: getOrderDetails,
    formatDetails,
    downloadReports,
  } = useOrdersAPI();
  const toaster = useToast(
    t('results_downloadToast1'),
    t('results_downloadToast2')
  );

  const timezone = useSelector(
    (state: IApplicationState) => state.projectPicker.timeZone
  );

  const isSampleLevelDocumentsEnabled = useSelector(
    (state: IApplicationState) => state.projectPicker.hasSampleLevelDocuments
  );

  const projectId = useSelector(
    (state: IApplicationState) => state.projectPicker.activeProjectId
  );

  useEffect(() => {
    if (projectId) {
      if (!isSampleLevelDocumentsEnabled) {
        setHiddenColumns([...hiddenColumns, 'reports']);
      } else {
        setHiddenColumns(hiddenColumns.filter((it) => it !== 'reports'));
      }
    }
  }, [projectId, isSampleLevelDocumentsEnabled]);

  // logic like this should not exist, the problem is occuring upwards
  useEffect(() => {
    if (currentOrder !== undefined) {
      setOrder(currentOrder);
    }
  }, [currentOrder]);

  useEffect(() => {
    async function getDetails() {
      setRequestStatus(RequestStatus.inProgress);
      const { objects: tableData } = await getOrderDetails(orderNumber);
      const data = formatDetails(tableData);

      setRequestStatus(RequestStatus.completed);
      setTableData(data);
    }
    getDetails();
  }, []);

  const showAsSelectableRow = React.useCallback((row: Row) => {
    const { reports } = row.original as SampleDetail;
    return reports.length > 0 && reports.some((it) => it.status === 'Active');
  }, []);

  const {
    isSelectionVisible,
    toggleSelection,
    selectedRows,
    toggleAllSelectedRows,
    render: renderResults,
  } = useResultsTable({
    columns: React.useMemo(
      () => getColumns(t, timezone, projectId as string),
      [hiddenColumns, projectId, timezone]
    ),
    data: React.useMemo(() => tableData, [tableData, hiddenColumns]),
    hiddenColumns: React.useMemo(() => [...hiddenColumns], [hiddenColumns]),
    sortBy: React.useMemo(() => [{ id: 'resultDate', desc: true }], []),
    getCustomCellProps: React.useCallback(getCustomCellProps, [
      getCustomCellProps,
    ]),
    showCheckboxPerRow: showAsSelectableRow,
    markSelectedRowsByCondition: React.useCallback((rows: Row[]) => {
      return rows.filter(showAsSelectableRow);
    }, []),
  });

  const downloadSelected = () => {
    let downloadReportsParams: IGetOrderReports = {
      documentTypes: [],
      orderTestIds: [],
    };

    selectedRows.forEach(({ original: { reports } }) => {
      if (reports.length > 0) {
        let activeReports = reports.filter((r) => r.status === 'Active');
        if (activeReports.length > 0) {
          const item: IDownloadSampleReport = activeReports[0] as any;
          downloadReportsParams.documentTypes.push(item.documentType);
          downloadReportsParams.orderTestIds.push(item.orderTestId);
        }
      }
    });

    const uniqueDocumentTypes = new Set(downloadReportsParams.documentTypes);

    downloadReports(
      Array.from(uniqueDocumentTypes),
      downloadReportsParams.orderTestIds
    );

    toggleAllSelectedRows(false);
  };

  const cancelDownload = () => {
    toggleSelection(true);
    toggleAllSelectedRows(false);
  };

  const loadHeader = () => {
    if (order && timezone) {
      return (
        <div className='selected-order-detail mt-2'>
          <Helmet>
            <title>{`${t('orderDetail_title_prefix')}${orderNumber}`}</title>
          </Helmet>
          <div className='page-header-1'>
            <Link
              className='order-detail-header-link pr-1'
              to={`${routes.app.orders}`}
            >
              <i className='fa fa-chevron-left fa-1x' aria-hidden='false'></i>
            </Link>
            <h1 className='ml-1'>
              {`Order #${orderNumber} (${order.description})`}
            </h1>
          </div>

          <table className='standard-table selected-order-table mt-2'>
            <thead>
              <tr>
                {headings.map((heading, index) => (
                  <th key={index} colSpan={heading.colSpan ?? 1}>
                    {heading.text}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr id='order-details-header-row'>
                <td className='bold text-uppercase'>
                  {translateStatus(t, order.status)}
                </td>
                <td className='bold'>{order.orderNumber}</td>
                <td className='bold'>{order.description}</td>

                <td className='border-right-0'>
                  <StageUploaded count={order.sampleSummary.uploaded} />
                </td>

                <td
                  className={
                    order.sampleSummary.isProcessingWithWarning
                      ? 'orders-stage-warning'
                      : ''
                  }
                >
                  <StageProcessing
                    count={order.sampleSummary.processing}
                    anyPassedProcessing={order.sampleSummary.finished > 0}
                  />
                </td>

                <td>
                  <StageFinished count={order.sampleSummary.finished} />
                </td>

                <td>
                  {order.sampleSummary.latestProcessed
                    ? formatDateWithTimezone(
                        order.sampleSummary.latestProcessed,
                        timezone
                      )
                    : ''}
                </td>

                <td>{formatDateWithTimezone(order.created, timezone)}</td>
              </tr>
            </tbody>
          </table>

          <div className='pt-3'>
            <ul className='nav nav-tabs' id='order-details-tab' role='tablist'>
              <li className='nav-item' role='presentation'>
                <a
                  className='nav-link active'
                  id='results-tab'
                  data-toggle='tab'
                  href='#results'
                  role='tab'
                  aria-controls='results'
                  aria-selected='true'
                >
                  {t('orderDetail_order_results')}
                </a>
              </li>
            </ul>
          </div>
        </div>
      );
    }
  };

  const loadContent = () => {
    return (
      <HOCLoader
        status={requestStatus}
        isEmpty={tableData.length === 0}
        customEmpty={
          <InvalidMessage
            title={t('orderDetail_not_found_main')}
            description={t('orderDetail_not_found_sub')}
          />
        }
      >
        <div className='tab-content pt-2'>
          <div
            className='orders-detail-list tab-pane fade show active'
            id='results'
            role='tabpanel'
            aria-labelledby='results-tab'
          >
            {isSampleLevelDocumentsEnabled ? (
              <div className='select-download-commands d-flex flex-row align-items-center'>
                {!isSelectionVisible ? (
                  <SelectAndDownload onClick={() => toggleSelection(false)} />
                ) : (
                  <>
                    <span className='text--type1 mr-2'>
                      {t('sampleList_results_selectToDownload')}
                    </span>
                    <button
                      className={classnames('btn btn-primary btn-sm', 'mr-2', {
                        disabled: selectedRows.length === 0,
                      })}
                      disabled={selectedRows.length === 0}
                      onClick={() => {
                        toaster.show();
                        toggleSelection(true);
                        downloadSelected();
                      }}
                    >
                      <span className='text-uppercase'>
                        {t('sampleList_results_downloadSelectedResults')}
                      </span>
                    </button>
                    <button
                      className='btn btn-light btn-sm'
                      onClick={() => cancelDownload()}
                    >
                      <span className='text-uppercase'>
                        {t('common_cancel')}
                      </span>
                    </button>
                  </>
                )}
              </div>
            ) : null}
            <div className='mt-2'>{renderResults}</div>
          </div>
          <>{toaster.render}</>
        </div>
      </HOCLoader>
    );
  };

  return (
    <div>
      {loadHeader()}
      {loadContent()}
    </div>
  );
};

export default OrderDetail;
